import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, Plus, ChevronRight, Trash2, UserCheck, UserX } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminAPI.createUser(newUser);
      toast.success('User created successfully');
      setShowAddDialog(false);
      setNewUser({ name: '', email: '', password: '' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) return;
    
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted');
      loadUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-users-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Users</h1>
            <p className="text-[#4A2C2A]/60">{users.length} total users</p>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter name"
                    required
                    data-testid="new-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email"
                    required
                    data-testid="new-user-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter password"
                    required
                    minLength={6}
                    data-testid="new-user-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={creating}
                  className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120]"
                  data-testid="create-user-btn"
                >
                  {creating ? 'Creating...' : 'Create User'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C2A]/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="input-warm pl-12"
            data-testid="search-users-input"
          />
        </div>

        {/* Users List */}
        <div className="soft-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FDFBF7]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#4A2C2A]/60">User</th>
                  <th className="text-left p-4 text-sm font-medium text-[#4A2C2A]/60">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-[#4A2C2A]/60">Joined</th>
                  <th className="text-right p-4 text-sm font-medium text-[#4A2C2A]/60">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8A095]/10">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-[#FDFBF7]/50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#E8A095]/20 flex items-center justify-center">
                          <span className="text-[#4A2C2A] font-medium">
                            {user.name?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#4A2C2A]">{user.name}</p>
                          <p className="text-sm text-[#4A2C2A]/50">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {user.onboarding_completed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#8DA399]/20 text-[#8DA399] rounded-full text-xs font-medium">
                          <UserCheck className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#E8C495]/20 text-[#E8C495] rounded-full text-xs font-medium">
                          <UserX className="w-3 h-3" /> Pending
                        </span>
                      )}
                      {user.is_admin && (
                        <span className="ml-2 px-2 py-1 bg-[#4A2C2A] text-white rounded-full text-xs font-medium">
                          Admin
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-[#4A2C2A]/60">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/users/${user.id}`}>
                          <Button variant="ghost" size="sm" className="text-[#4A2C2A]" data-testid={`view-user-${user.id}`}>
                            View <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                        {!user.is_admin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="text-[#D68282] hover:text-[#D68282] hover:bg-[#D68282]/10"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="p-12 text-center text-[#4A2C2A]/50">
              {search ? 'No users match your search' : 'No users found'}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
